import { clamp } from './curve'
import { G, SIDES, sampleAngle, type GaitResult, type Side } from './gait'
import {
  buildSkeleton,
  contactPoints,
  nodePoint,
  poseAt,
  soleHeight,
  type AngleLookup,
  type FrameKey,
  type NodeKey,
  type PelvisPlacement,
  type Pose,
  type Skeleton,
} from './kinematics'
import { ownMass, type HumanoidModel, type SegmentKey } from './model'
import { cycleSteps, isMove } from './moves'
import { stanceFraction } from './reference'
import { carrying, type SimState } from './state'
import { lerpVec, type Vec3 } from './vec'

/**
 * Bodenkontakt — wo der Körper WIRKLICH steht.
 *
 * DIE STUFE, DIE MAN BEIM PORTIEREN ÜBERSIEHT.
 *
 * Die Gangerzeugung liefert Gelenkwinkel und eine VORGEGEBENE Beckenbahn. Beides
 * zusammen ergibt noch keinen Roboter, der auf dem Boden steht: die vorgegebene
 * Bahn und die Beinlänge stimmen nicht überein, weil die Bahn aus einem
 * Pendelmodell kommt und das Bein aus Gelenkpunkten. Die Differenz sind hier
 * einige Zentimeter — der belastete Fuss schwebt oder steckt im Boden.
 *
 * Das ist keine Schönheitsfrage. Aus der Bodenreaktionskraft folgt jedes Moment
 * und daraus jede Antriebsauslegung. Ein Fuss, der 5 cm über dem Boden steht,
 * trägt in Wahrheit nichts, und die Kraft, die er scheinbar aufnimmt, verteilt
 * sich falsch. Die Vorlage hält das als eigene Prüfgrösse fest: Bodenfehler
 * (Durchdringung) und Schweben des belasteten Fusses.
 *
 * DREI SCHRITTE:
 *
 *   1. Zwangsbedingung — mit dem Becken auf Ruhehöhe: wie tief liegt der
 *      tiefste Sohlenpunkt? Das ist der Versatz, den die Höhenlage braucht.
 *      Geglättet, weil ein starres Bein sonst jede Sohlenkante hart abbildet.
 *
 *   2. Höhenbahn — die vorgegebene Form bleibt erhalten, die Zwangsbedingung
 *      gibt die Lage. Gemischt wird über die GLÄTTUNG DER DIFFERENZ: bei null
 *      Durchgängen folgt die Bahn der Zwangsbedingung Punkt für Punkt (mit
 *      allen Knicken), bei vielen bleibt die vorgegebene Form und nur ihr
 *      Mittelwert wandert. In der Flugphase wird die Bahn durch eine
 *      Wurfparabel ersetzt — dort wirkt nur die Schwerkraft, und eine
 *      vorgegebene Bahn verlangte sonst eine Kraft, die es nicht gibt.
 *
 *   3. Standbein-Nachführung — Knie, Sprunggelenk und aushilfsweise die Hüfte
 *      räumen den Rest weg, damit der belastete Fuss den Boden berührt. Die
 *      Korrektur wird am Sprunggelenk zurückgedreht, sonst rollt der Fuss mit
 *      ihr auf und ab statt gleichmässig über Ferse, Ballen und Zehe.
 */

/* -------------------------------------------------------------------------
   Zahlen des Kontaktmodells
   ------------------------------------------------------------------------- */

/**
 * Alles, was das Kontaktmodell an Zahlen braucht — als Daten, nicht als Formel.
 *
 * Sie stehen beisammen, weil sie zusammen eingestellt werden: wer die
 * Nachgiebigkeit erhöht, muss meist auch die Glättung der Höhenbahn anfassen.
 */
export interface ContactSpec {
  /**
   * Glättungsdurchgänge der Zwangsbedingung — die Nachgiebigkeit des Standbeins
   * (Fersenpolster, Sohle, Struktur). Ein starres Bein bildet jede Sohlenkante
   * hart ab, und die zweite Ableitung davon dominiert dann jedes Moment.
   */
  compliance: number
  /**
   * Glättungsdurchgänge der DIFFERENZ zwischen Zwangsbedingung und vorgegebener
   * Bahn. 0 = die Bahn folgt der Zwangsbedingung samt Knicken, gross = die
   * vorgegebene Form bleibt und das Standbein gleicht mehr aus. 24 hält beides
   * klein: Restabweichung wenige Millimeter, Korrektur unter 4°.
   */
  fit: number
  /** Randbereich um die Standphase, in dem noch nachgeführt wird. */
  margin: number
  /** Grenzhöhe, unter der ein Sohlenpunkt als Bodenkontakt zählt, m. */
  contactHeight: number
  /** Wie stark Knie und Sprunggelenk gegenüber der Hüfte bevorzugt werden. */
  hipShare: number
  /** Grenze der Knie-/Sprunggelenkkorrektur, rad. */
  kneeLimit: number
  /** Grenze der Hüftkorrektur, rad. */
  hipLimit: number
  /** Zielbeschleunigung der seitlichen Beckenverlagerung, m/s². */
  swayAcceleration: number
  /** Grösste seitliche Verlagerung, m. */
  swayMax: number
  /** Phasenvorlauf der seitlichen Verlagerung, Anteil des Zyklus. */
  swayLead: number
}

export const CONTACT: ContactSpec = {
  compliance: 15,
  fit: 24,
  margin: 0.07,
  contactHeight: 0.01,
  hipShare: 0.1,
  kneeLimit: 0.35,
  hipLimit: 0.3,
  swayAcceleration: 1.2,
  swayMax: 0.05,
  swayLead: 0.05,
}

/**
 * Nachgiebigkeit ohne den Schalter `comply` — steif, aber nicht null.
 *
 * Zwei Durchgänge und nicht keiner: die Zwangsbedingung wird aus einem
 * abgetasteten Sohlenpunkt gebildet, und ihre ZWEITE Ableitung geht in jedes
 * Moment ein. Ganz ohne Glättung stünde dort das Abtastrauschen und nicht die
 * Steifigkeit des Beins — man sähe einen Unterschied, aber den falschen.
 */
const RIGID_COMPLIANCE = 2

/**
 * Zu welchem Anteil der Kopf der Rumpfdrehung folgt, wenn stabilisiert wird.
 *
 * 0,35 aus der Vorlage. Nicht null: ein Kopf, der der Rumpfdrehung gar nicht
 * folgte, stünde im Raum fest und verlangte am Hals genau die Gegendrehung des
 * Rumpfes — mehr, als ein Halsgelenk hergibt.
 */
const GAZE_FOLLOW = 0.35

/** Nachgiebigkeit und Zielbeschleunigung je Gangart. */
export function contactSpec(state: SimState): ContactSpec {
  const spec = ((): ContactSpec => {
    switch (state.gait) {
      case 'sprint':
        return { ...CONTACT, compliance: 16, swayAcceleration: 2.0 }
      case 'run':
        return { ...CONTACT, compliance: 14, swayAcceleration: 1.6 }
      case 'carry':
        return { ...CONTACT, swayAcceleration: 1.0 }
      case 'stand':
        return { ...CONTACT, swayAcceleration: 0 }
      default:
        return { ...CONTACT }
    }
  })()
  /* Fersenpolster, Sohle und Weichteile — abschaltbar. Aus heisst: starres Bein,
     und jede Sohlenkante schlägt voll auf die Rumpfbahn durch. */
  return state.features.comply ? spec : { ...spec, compliance: RIGID_COMPLIANCE }
}

/**
 * Die Korrektur wird am Sprunggelenk zurückgedreht.
 *
 * Knie und Hüfte gehen mit umgekehrtem Vorzeichen ins Sprunggelenk, damit die
 * Fussneigung von der Nachführung unberührt bleibt. Ohne das rollt der Fuss mit
 * der Korrektur auf und ab — und die Abrollbewegung, die das ganze Kontaktmodell
 * trägt, ist dahin.
 */
const KNEE_TO_ANKLE = -1
const HIP_TO_ANKLE = -1
/** Vorzeichen, mit dem die Kniekorrektur in den Kniewinkel eingeht. */
const KNEE_SIGN = -1

/* -------------------------------------------------------------------------
   Glättung
   ------------------------------------------------------------------------- */

/** Zyklische [1,2,1]-Glättung über `count` Werte. */
function smoothCyclic(values: Float32Array, count: number, passes: number): void {
  if (passes <= 0) return
  const buffer = new Float32Array(count)
  for (let p = 0; p < passes; p++) {
    for (let i = 0; i < count; i++) buffer[i] = values[i]
    for (let i = 0; i < count; i++) {
      values[i] =
        0.25 * buffer[(i - 1 + count) % count] + 0.5 * buffer[i] + 0.25 * buffer[(i + 1) % count]
    }
  }
}

const smoothstep = (x: number): number => {
  const t = clamp(x, 0, 1)
  return t * t * (3 - 2 * t)
}

/* -------------------------------------------------------------------------
   Ergebnis
   ------------------------------------------------------------------------- */

export interface GroundResult {
  samples: number
  cycleTime: number
  skeleton: Skeleton
  spec: ContactSpec
  /** Höhenversatz des Beckens gegenüber der Ruhehöhe, je Stützstelle, m. */
  lift: Float32Array
  /** Vorwärtsversatz des Beckens im mitlaufenden Bezug, m. */
  forward: Float32Array
  /** Seitliche Beckenverlagerung, m; zur rechten Körperseite positiv. */
  lateral: Float32Array
  /** Nachführung von Knie und Sprunggelenk je Seite, rad. */
  kneeFix: Record<Side, Float32Array>
  /** Aushilfe der Hüfte je Seite, rad. */
  hipFix: Record<Side, Float32Array>
  /** Gemessener Bodenkontakt je Fuss, 0 … 1. */
  contact: Record<Side, Float32Array>
  /** Fertige Posen — das Ergebnis, mit dem die Dynamik weiterrechnet. */
  poses: Pose[]
  /** Tiefste Durchdringung des Bodens über den Zyklus, m (≤ 0). */
  penetration: number
  /** Grösstes Schweben eines voll belasteten Fusses, m (≥ 0). */
  float: number
}

/* -------------------------------------------------------------------------
   Der Durchlauf
   ------------------------------------------------------------------------- */

/**
 * Setzt den Körper auf den Boden.
 *
 * Rein: gleiche Eingaben, gleiches Ergebnis. Keine Szene, kein Zustand
 * ausserhalb der Argumente.
 */
export function computeGround(
  model: HumanoidModel,
  state: SimState,
  gait: GaitResult,
): GroundResult {
  const n = gait.samples
  const skeleton = buildSkeleton(model)
  const spec = contactSpec(state)
  /* Der Standphasenanteil kommt beim gerechneten Gang aus der Aufgabe, beim
     aufgezeichneten aus dem Gangbild. Eine Stelle, nicht zwei. */
  const stance = stanceFraction(state)

  const kneeFix: Record<Side, Float32Array> = {
    R: new Float32Array(n),
    L: new Float32Array(n),
  }
  const hipFix: Record<Side, Float32Array> = {
    R: new Float32Array(n),
    L: new Float32Array(n),
  }

  /* --- Seitliche Verlagerung -------------------------------------------- */
  /*
    Die Amplitude wird aus einer ZIELBESCHLEUNIGUNG gerechnet (A = a/ω²), nicht
    fest vorgegeben. Bei fester Amplitude wüchse die Beschleunigung mit 1/T², und
    bei hoher Kadenz explodierten die waagerechten Bodenreaktionskräfte, ohne
    dass irgendwo eine Zahl falsch aussähe.
  */
  const lateral = new Float32Array(n)
  const omega = (2 * Math.PI) / gait.cycleTime
  const amplitude =
    clamp(spec.swayAcceleration / (omega * omega), 0, spec.swayMax) * state.trim.pelvis
  for (let i = 0; i < n; i++) {
    lateral[i] = amplitude * Math.sin(2 * Math.PI * (i / n - spec.swayLead))
  }

  /* --- Schritt 1: Zwangsbedingung ---------------------------------------- */
  const angles = (i: number, side?: Side): AngleLookup => {
    const phase = i / n
    return (joint, plane, s) => {
      const base = sampleAngle(gait, joint, plane, s, phase) ?? 0
      if (plane !== 'sag') return base
      /* Die Nachführung wirkt nur auf der Seite, für die sie gerade gesucht
         wird — sonst rechnet die Suche gegen die andere Seite mit. */
      if (side !== undefined && s !== side) return base
      const k = (kneeFix[s][i] * 180) / Math.PI
      const h = (hipFix[s][i] * 180) / Math.PI
      if (joint === 'hip') return base + h
      if (joint === 'knee') return base + k * KNEE_SIGN
      if (joint === 'ankle') return base + k * KNEE_TO_ANKLE + h * HIP_TO_ANKLE
      return base
    }
  }

  const placement = (i: number, lift: number): PelvisPlacement => ({
    x: 0,
    y: skeleton.pelvisRest + lift,
    z: lateral[i],
  })

  /*
    BLICKSTABILISIERUNG — der Schalter `gaze`. Aus heisst: der Kopf sitzt starr
    am Brustkorb und macht jede Rumpfdrehung voll mit. An heisst: er folgt ihr
    zu 35 %, und die Kopfmasse dreht entsprechend weniger — was sich im Drall um
    die Hochachse und in den Momenten der Rumpfkette niederschlägt.

    Ein Wert und nicht zwei Zweige: `1` ist der starre Fall, und der Ausdruck
    darüber gilt für beide.
  */
  const poseOptions = { headFollow: state.features.gaze ? GAZE_FOLLOW : 1 }

  /*
    WIE VIELE GLEICHE ABSCHNITTE DER ZYKLUS HAT — zwei beim Gang, einer bei
    gleichphasigen Beinen. Daran hängt die halbe Kontaktrechnung.

    Beim Gang macht das linke Bein eine halbe Periode später, was das rechte
    gerade macht; ein Zyklus enthält deshalb zwei Standphasen, und beide Beine
    zusammen tragen `2 × Standphasenanteil`. Beim Hackensprung springen beide
    zugleich: ein Abschnitt, und die Tragzeit ist der Standphasenanteil selbst.

    Ohne diese Unterscheidung rechnet der Sprung mit 1,36 Tragzeit, kommt über
    eins und verlässt den Boden nie — Bodenkraft 1,01 Körpergewicht bei einem
    Sprung. Genau so sah der erste Durchlauf aus.
  */
  const steps = cycleSteps(state.gait)
  const segment = 1 / steps
  /** Anteil des Abschnitts, in dem überhaupt ein Fuss trägt. */
  const support = stance * steps

  /* Beim Gang läuft die linke Seite eine halbe Periode später; bei einer
     gleichphasigen Bewegung nicht. */
  const sidePhase = (t: number, side: Side): number =>
    side === 'R' || steps === 1 ? t : (t + 0.5) % 1

  const constraint = new Float32Array(n)
  for (let i = 0; i < n; i++) {
    const pose = poseAt(skeleton, angles(i), placement(i, 0), poseOptions)
    constraint[i] = -Math.min(soleHeight(skeleton, pose, 'R'), soleHeight(skeleton, pose, 'L'))
  }
  smoothCyclic(constraint, n, spec.compliance)

  /* --- Schritt 2: Höhenbahn ---------------------------------------------- */
  const lift = new Float32Array(n + 1)
  const forward = new Float32Array(n + 1)

  if (state.generated && state.gait !== 'stand') {
    /*
      GERECHNETER GANG: die Rumpfbahn ist vorgegeben und wird NICHT aus dem
      Kontakt zurückgerechnet — sonst stünden zwei Höhenbahnen gegeneinander und
      die Gelenke müssten die Differenz als Blindarbeit aufbringen.

      `lift = vorgegeben + geglättete Differenz`. Bei null Durchgängen bleibt
      genau die Zwangsbedingung übrig, bei vielen die vorgegebene Form mit dem
      richtigen Mittelwert. Ein Ausdruck für beide Grenzfälle — kein Umschalten,
      keine zweite Formel.
    */
    const difference = new Float32Array(n)
    for (let i = 0; i < n; i++) difference[i] = constraint[i] - gait.hipTrack[i]
    smoothCyclic(difference, n, spec.fit)
    for (let i = 0; i < n; i++) lift[i] = gait.hipTrack[i] + difference[i]
  } else if (!isMove(state.gait) && support < 0.999) {
    /*
      AUFGEZEICHNETES RENNEN: die Rumpfbahn folgt dem Feder-Masse-Modell.
      Standphase eine halbe Sinuswelle (das Bein federt ein und wieder aus),
      Flugphase eine Wurfparabel. Beide sind in Höhe UND Geschwindigkeit stetig
      verbunden; die Flugdauer folgt aus der Abfluggeschwindigkeit, und damit ist
      die Einfedertiefe eindeutig bestimmt — sie ist keine freie Wahl.
    */
    const stanceTime = stance * gait.cycleTime
    const flightTime = segment * gait.cycleTime - stanceTime
    const v0 = (G * flightTime) / 2
    const dip = (v0 * stanceTime) / Math.PI

    /* Höhenlage so wählen, dass das Standbein im Mittel weder gestaucht noch
       überstreckt wird. */
    let sum = 0
    let count = 0
    for (let i = 0; i < n; i++) {
      const phase = i / n
      if (phase >= stance) continue
      sum += constraint[i] + dip * Math.sin((Math.PI * phase) / stance)
      count++
    }
    const level = count > 0 ? sum / count : 0

    for (let i = 0; i < n; i++) {
      const phase = (i / n) % segment /* alle Abschnitte sind gleich */
      if (phase < stance) {
        lift[i] = level - dip * Math.sin((Math.PI * phase) / stance)
      } else {
        const tau = (phase - stance) * gait.cycleTime
        lift[i] = level + v0 * tau - 0.5 * G * tau * tau
      }
    }
  } else {
    /*
      AUFGEZEICHNETES GEHEN UND SKRIPTBEWEGUNGEN: die Rumpfbahn folgt der
      Beinlänge.

      WARUM EINE SKRIPTBEWEGUNG HIER STEHT UND NICHT BEIM RENNEN, obwohl sie
      eine Flugphase hat: das Feder-Masse-Modell rechnet die Einfedertiefe aus
      der Flugzeit — es SETZT die Rumpfbahn, weil beim Rennen die Winkelkurven
      sie nicht bestimmen. Bei einem Skript bestimmen sie sie sehr wohl; die
      Hocke steht als Knie- und Hüftwinkel darin. Beides zugleich ergibt zwei
      Höhenbahnen gegeneinander: für den Hackensprung verlangte das Federmodell
      489 mm Einfederung, das Skript liefert weit weniger, und die Sohle stand
      260 mm unter dem Boden.

      Die Flugphase kommt trotzdem — sie wird unten für JEDE Bewegung ohne
      Bodenkontakt korrigiert, gleichgültig woher die Winkel stammen.

      Das `Math.max` ist der Kern: die Bahn darf nirgends unter die
      Zwangsbedingung fallen, sonst sackt der Körper durch das Schwungbein. Es
      erzeugt aber Knicke, und an einem Knick explodiert die zweite Ableitung —
      deshalb wird danach noch einmal kräftig geglättet.
    */

    for (let i = 0; i < n; i++) lift[i] = constraint[i]
    smoothCyclic(lift, n, 4)
    for (let i = 0; i < n; i++) lift[i] = Math.max(lift[i], constraint[i])
    smoothCyclic(lift, n, 14)
  }

  /*
    FLUGPHASE: es trägt kein Fuss, also wirkt nur die Schwerkraft. Der
    SCHWERPUNKT beschreibt eine Wurfparabel und läuft waagerecht gleichförmig;
    die Beckenhöhe wird daraus zurückgerechnet, weil der Schwerpunkt mit den
    Beinen wandert.

    ABWEICHUNG VON DER VORLAGE, BEWUSST: dort steht diese Korrektur nur im
    gerechneten Gang. Ihre Begründung gilt aber für jede Flugphase — ein Körper
    ohne Bodenkontakt fällt frei, gleichgültig woher seine Gelenkwinkel stammen.
    Ohne sie fehlten beim aufgezeichneten Rennen 14 % und beim Sprint 21 % des
    senkrechten Kraftimpulses: die vorgegebene Bahn verlangte im Flug eine Kraft,
    die es nicht gibt, und die Bodenkraft wurde entsprechend zu klein. Der Fehler
    ist an der Impulsbilanz messbar und verschwindet mit der Korrektur.
  */
  if (state.gait !== 'stand' && support < 0.999) {
    const offset: Vec3[] = []
    for (let i = 0; i < n; i++) {
      offset.push(comOffset(model, state, skeleton, angles(i), placement(i, 0)))
    }
    const half = n / steps
    const flightStart = Math.round(support * half)
    const dt = gait.cycleTime / n
    for (let k = 0; k < steps; k++) {
      const a = k * half + flightStart
      const b = (k + 1) * half
      if (b <= a + 1) continue
      const ya = lift[a % n] + offset[a % n].y
      const yb = lift[b % n] + offset[b % n].y
      const xa = offset[a % n].x
      const xb = offset[b % n].x
      const flightTime = (b - a) * dt
      const v0 = (yb - ya) / flightTime + (G * flightTime) / 2
      for (let i = a + 1; i < b; i++) {
        const tau = (i - a) * dt
        const u = tau / flightTime
        lift[i % n] = ya + v0 * tau - 0.5 * G * tau * tau - offset[i % n].y
        forward[i % n] = xa + (xb - xa) * u - offset[i % n].x
      }
    }
  }
  lift[n] = lift[0]
  forward[n] = forward[0]

  /* --- Schritt 3: Standbein-Nachführung ---------------------------------- */
  /*
    Zwei Unbekannte (Knie/Sprunggelenk und Hüfte), eine Gleichung (Sohlenhöhe).
    Gelöst über eine GEWICHTETE Pseudoinverse: Knie und Sprunggelenk führen, die
    Hüfte hilft nur dort aus, wo die Beinstellung das Knie unwirksam macht — im
    Fersenaufsatz, wo das Bein fast gestreckt ist und eine Kniebeugung die
    Sohlenhöhe kaum noch ändert.
  */
  const step = 0.02
  /*
    Beim aufgezeichneten GEHEN trägt immer ein Fuss und die Rumpfbahn folgt exakt
    der Beinlänge — dort gibt es nichts auszugleichen (Restfehler unter 2 mm).
    Überall sonst ist die Rumpfbahn vorgegeben und das Standbein muss die
    Differenz aufbringen.
  */
  const needsFix = state.generated || isRunning(state) || state.gait === 'stand'

  for (const side of SIDES) {
    const k = kneeFix[side]
    const h = hipFix[side]

    for (let i = 0; i < n; i++) {
      if (!needsFix) break
      const phase = sidePhase(i / n, side)
      const inside = phase < stance
      const margin = !inside && (phase < stance + spec.margin || phase > 1 - spec.margin)
      if (!inside && !margin) continue

      const measure = (): number =>
        soleHeight(skeleton, poseAt(skeleton, angles(i, side), placement(i, lift[i])), side)

      /* Im Randbereich wird nur nach unten korrigiert: ein Fuss, der dort über
         dem Boden schwebt, ist richtig — er hebt gerade ab. */
      if (margin && measure() > 0) continue

      for (let it = 0; it < 5; it++) {
        const e0 = measure()
        if (Math.abs(e0) < 2e-4) break

        k[i] += step
        const ek = measure()
        k[i] -= step

        h[i] += step
        const eh = measure()
        h[i] -= step

        const gk = (ek - e0) / step
        const gh = (eh - e0) / step
        const norm = gk * gk + spec.hipShare * gh * gh
        if (norm < 1e-6) break

        k[i] = clamp(k[i] - (e0 * gk) / norm, -spec.kneeLimit, spec.kneeLimit)
        h[i] = clamp(h[i] - (e0 * spec.hipShare * gh) / norm, -spec.hipLimit, spec.hipLimit)
      }
    }

    /* Weicher Ein- und Auslauf statt eines Knicks an den Rändern der Standphase,
       danach glätten: die Korrektur ist ein kleiner Zusatz zur Kurve, und
       ungeglättet bringt sie Knicke mit, die in der zweiten Ableitung — und
       damit im Moment — um Grössenordnungen verstärkt werden. */
    for (const array of [k, h]) {
      for (let i = 0; i < n; i++) {
        let phase = sidePhase(i / n, side)
        if (phase > 1 - spec.margin) phase -= 1
        const d = clamp(
          Math.min(phase + spec.margin, stance + spec.margin - phase) / (spec.margin * 0.9),
          0,
          1,
        )
        array[i] *= smoothstep(d)
      }
      smoothCyclic(array, n, 3)
    }
  }

  /* --- Ergebnis und Prüfgrössen ------------------------------------------ */
  const poses: Pose[] = []
  for (let i = 0; i < n; i++) {
    poses.push(
      poseAt(skeleton, angles(i), { ...placement(i, lift[i]), x: forward[i] }, poseOptions),
    )
  }

  const contact: Record<Side, Float32Array> = {
    R: new Float32Array(n),
    L: new Float32Array(n),
  }
  let penetration = 0
  let float = 0
  for (let i = 0; i < n; i++) {
    for (const side of SIDES) {
      const low = soleHeight(skeleton, poses[i], side)
      contact[side][i] = low < spec.contactHeight ? 1 : 0

      const phase = sidePhase(i / n, side)
      if (phase > stance) continue
      if (low < penetration) penetration = low
      /* Abheben ist genauso falsch wie Durchdringen: ein voll belasteter Fuss,
         der über dem Boden steht, trägt in Wahrheit nichts. Nur voll belastete
         Proben zählen — der Übergang darf abheben. */
      if (stanceWeight(phase, stance, state.generated, isRunning(state)) > 0.9 && low > float) {
        float = low
      }
    }
  }
  for (const side of SIDES) smoothCyclic(contact[side], n, 4)

  return {
    samples: n,
    cycleTime: gait.cycleTime,
    skeleton,
    spec,
    lift,
    forward,
    lateral,
    kneeFix,
    hipFix,
    contact,
    poses,
    penetration,
    float,
  }
}

/**
 * Lastanteil eines Fusses über den Zyklus.
 *
 * DAS FENSTER MUSS BREITER SEIN ALS DIE STANDPHASE.
 *
 * Die Bahnen sind gefiltert; der Übergang vom Stand in den Flug ist danach nicht
 * mehr ein Punkt, sondern ein paar Stützstellen breit. Fällt das Lastfenster
 * genau bei der Standphasengrenze auf null, wird die Kraft dieser Übergangsstelle
 * verworfen — sie fehlt in der Impulsbilanz und taucht als Blindarbeit in den
 * Gelenken wieder auf.
 *
 * ABWEICHUNG VON DER VORLAGE, BEWUSST: dort wird das Fenster nur im gerechneten
 * Gang verbreitert. Beim aufgezeichneten GEHEN spielt das keine Rolle, weil immer
 * ein Fuss trägt und die Last ohnehin auf eins normiert wird. Beim
 * aufgezeichneten RENNEN spielt es sehr wohl eine Rolle: dort fehlten ohne die
 * Verbreiterung 13 % des senkrechten Kraftimpulses, beim Sprint 17 %. Die
 * Begründung der Vorlage gilt wörtlich auch hier — sie war dort nur nicht
 * angewandt.
 */
export function stanceWeight(
  phase: number,
  duty: number,
  generated: boolean,
  running = false,
): number {
  if (duty >= 1) return 1
  /* Halbe Breite der Verbreiterung, als Anteil des Zyklus — sie entspricht der
     Glättungsbreite der Bahnen. Nur nötig, wo es eine Flugphase gibt. */
  const e = generated || running ? 0.045 : 0
  const q = (phase + e) % 1
  const window = duty + 2 * e
  if (q > window) return 0

  /* Beim aufgezeichneten Rennen ist der Abfall länger als der Anstieg: der Fuss
     setzt schnell auf und löst sich langsam über den Vorfuss ab. Beim
     gerechneten Gang ist beides gleich — dort macht die Verbreiterung des
     Fensters den Unterschied. */
  const rise = window * (generated ? 0.25 : running ? 0.16 : 0.2)
  const fall = window * (generated ? 0.25 : running ? 0.34 : 0.2)
  return smoothstep(q / rise) * (1 - smoothstep((q - (window - fall)) / fall))
}

/* -------------------------------------------------------------------------
   Schwerpunkt gegenüber dem Becken
   ------------------------------------------------------------------------- */

/**
 * Wo der Schwerpunkt gegenüber dem Beckenknoten liegt — allein aus den
 * Gelenkwinkeln.
 *
 * Von der Höhenlage des Körpers unabhängig, und genau deshalb als Korrekturgrösse
 * für die Flugphase brauchbar: dort ist die SCHWERPUNKTBAHN vorgegeben (eine
 * Wurfparabel), und gesucht ist die Beckenhöhe. Die Beine wandern dabei
 * gegenüber dem Becken, also muss dieser Versatz herausgerechnet werden.
 */
export function comOffset(
  model: HumanoidModel,
  state: SimState,
  skeleton: Skeleton,
  angles: AngleLookup,
  placement: PelvisPlacement,
): Vec3 {
  const pose = poseAt(skeleton, angles, placement)

  let mass = 0
  let x = 0
  let y = 0
  let z = 0
  for (const spec of segmentSpecs(state)) {
    const m = segmentMass(model, state, spec.key)
    const p = segmentPoint(model, skeleton, spec, pose)
    mass += m
    x += m * p.x
    y += m * p.y
    z += m * p.z
  }

  const origin = pose.pelvis.position
  return {
    x: x / mass - origin.x,
    y: y / mass - origin.y,
    z: z / mass - origin.z,
  }
}

/* -------------------------------------------------------------------------
   Die Segmente — EINE Aufzählung für den ganzen Kern
   ------------------------------------------------------------------------- */

/**
 * Woran ein Segment hängt und wie es liegt — als DATEN.
 *
 * Steht hier und wird von der Dynamik mitbenutzt. Zwei Aufzählungen derselben
 * Segmente wären zwei Massenbilanzen, und die laufen beim ersten neuen Bauteil
 * auseinander, ohne dass es jemand merkt.
 *
 * `proximal` und `distal` sind die beiden Punkte, zwischen denen der Schwerpunkt
 * liegt. Fallen sie zusammen, ist das Segment eine Punktmasse an diesem Punkt.
 *
 * Namen statt Funktionen, weil die Dynamik die Bahnen erst filtern muss, bevor
 * sie ableitet — und das geht nur über eine Tabelle benannter Bahnen.
 */
export interface SegmentSpec {
  key: SegmentKey
  side: Side | null
  /** Eindeutiger Name der Bahn: `thighR`, `trunk`, … */
  name: string
  proximal: NodeKey
  distal: NodeKey
  frame: FrameKey
}

/**
 * Die paarigen Glieder.
 *
 * ACHTUNG BEIM FUSS: distal ist der BALLEN, nicht das Zehengrundgelenk. Der
 * Fussschwerpunkt liegt auf der Strecke Sprunggelenk → Ballen; das Gelenk sitzt
 * gut einen Zentimeter darüber, und mit ihm läge der Schwerpunkt zu hoch.
 */
const LIMBS: Omit<SegmentSpec, 'side' | 'name'>[] = [
  { key: 'thigh', proximal: 'hip', distal: 'knee', frame: 'hip' },
  { key: 'shank', proximal: 'knee', distal: 'ankle', frame: 'knee' },
  { key: 'foot', proximal: 'ankle', distal: 'ball', frame: 'ankle' },
  { key: 'upperArm', proximal: 'shoulder', distal: 'elbow', frame: 'shoulder' },
  { key: 'forearm', proximal: 'elbow', distal: 'wrist', frame: 'elbow' },
]

/** Alle Segmente, die für diesen Zustand in die Massenbilanz eingehen. */
export function segmentSpecs(state: SimState): SegmentSpec[] {
  const out: SegmentSpec[] = []
  for (const side of SIDES) {
    for (const limb of LIMBS) out.push({ ...limb, side, name: `${limb.key}${side}` })
  }

  const point = (key: SegmentKey, at: NodeKey): SegmentSpec => ({
    key,
    side: null,
    name: key,
    proximal: at,
    distal: at,
    frame: 'chest',
  })

  out.push(point('trunk', 'trunk'))
  out.push(point('head', 'head'))
  if (carrying(state)) out.push(point('payload', 'hands'))
  return out
}

/**
 * Masse eines Segments.
 *
 * Die Eigenmasse steht im Modell (Stückliste), die Zuladung im Zustand — sie ist
 * eine Eingabe und keine Konstruktionsgrösse.
 */
export const segmentMass = (model: HumanoidModel, state: SimState, key: SegmentKey): number =>
  key === 'payload' ? state.load : model.masses[key].kg

/** Der Schwerpunkt eines Segments in einer Pose. */
export function segmentPoint(
  model: HumanoidModel,
  skeleton: Skeleton,
  spec: SegmentSpec,
  pose: Pose,
): Vec3 {
  const side = spec.side ?? 'R'
  const a = nodePoint(skeleton, pose, spec.proximal, side)
  if (spec.proximal === spec.distal) return a
  const b = nodePoint(skeleton, pose, spec.distal, side)
  return lerpVec(a, b, model.masses[spec.key].com)
}

/** Läuft oder sprintet der Roboter? */
export const isRunning = (state: SimState): boolean =>
  state.gait === 'run' || state.gait === 'sprint'

/** Gesamtmasse einschliesslich Zuladung, kg. */
export const totalMass = (model: HumanoidModel, state: SimState): number =>
  ownMass(model) + (carrying(state) ? state.load : 0)

/** Nur zur Gegenprobe: die drei Bodenpunkte eines Fusses. */
export const footContact = contactPoints
